/**
 * @fileoverview ShareModal component for displaying and sharing invitation links in the Storyteller web application.
 * This component provides functionality to copy a share link to clipboard and share via email.
 *
 * @example
 * // Basic usage
 * <ShareModal
 *   shareConfig={{
 *     invitation_url: "https://example.com/invite/123"
 *   }}
 * />
 */


import { useTranslation } from 'react-i18next'
import css from './ShareModal.module.css'

/**
 * @interface ShareModalProps
 * @description Props for the ShareModal component.
 * @property {Object} shareConfig - The configuration object for the share modal.
 * @property {string} shareConfig.invitation_url - The URL to be shared.
 */
interface ShareModalProps {
    shareConfig: { invitation_url: string }
}

/**
 * @function ShareModal
 * @description Component for displaying and sharing invitation links in the Storyteller web application.
 * @param {ShareModalProps} props - The props for the ShareModal component.
 * @returns {JSX.Element} The ShareModal component.
 */
export function ShareModal({ shareConfig }: ShareModalProps) {
    const { t } = useTranslation()

    console.log(shareConfig)

    function onShareViaEmail() {
        window.open(
            `mailto:?subject=${t('share.email.subject')}&body=${t('share.email.body', {
                link: shareConfig.invitation_url,
                interpolation: { escapeValue: false },
            })}`
        )
    }

    function onCopyLink() {
        navigator.clipboard.writeText(shareConfig.invitation_url)
    }

    return (
        <div className={css.modal}>
            <h1 className={css.header}>{t('share.title')}</h1>
            <input
                type="text"
                className={css.link}
                value={shareConfig.invitation_url}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button className={`button confirm ${css.button}`} onClick={onCopyLink}>
                <span>{t('share.buttons.copy')}</span>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="#fff"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M7 9C7 7.89543 7.89543 7 9 7H15C16.1046 7 17 7.89543 17 9V15C17 16.1046 16.1046 17 15 17H9C7.89543 17 7 16.1046 7 15V9Z" />
                    <path d="M5 3C3.89543 3 3 3.89543 3 5V11C3 12.1046 3.89543 13 5 13L5 5H13C13 3.89543 12.1046 3 11 3H5Z" />
                </svg>
            </button>

            <div className={css.divider}>
                <div className={css.line}></div>
                <div className={css.or}>{t('share.divider')}</div>
                <div className={css.line}></div>
            </div>

            <button className={`button cancel ${css.button}`} onClick={onShareViaEmail}>
                <span>{t('share.buttons.email')}</span>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M2.003 5.884L10 9.882L17.997 5.884C17.9674 5.37444 17.7441 4.89549 17.3728 4.54523C17.0016 4.19497 16.5104 3.99991 16 4H4C3.48958 3.99991 2.99845 4.19497 2.62718 4.54523C2.25591 4.89549 2.0326 5.37444 2.003 5.884Z"
                        fill="#111827"
                    />
                    <path
                        d="M18 8.118L10 12.118L2 8.118V14C2 14.5304 2.21071 15.0391 2.58579 15.4142C2.96086 15.7893 3.46957 16 4 16H16C16.5304 16 17.0391 15.7893 17.4142 15.4142C17.7893 15.0391 18 14.5304 18 14V8.118Z"
                        fill="#111827"
                    />
                </svg>
            </button>
        </div>
    )
}
